import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import {
    type AnchorHTMLAttributes,
    type ButtonHTMLAttributes,
    forwardRef,
    type MouseEventHandler,
    type ReactNode,
} from 'react';
import { Link, type LinkProps } from 'react-router-dom';
import LoadingSpinner from '../loadingSpinner/LoadingSpinner';
import type { Merge } from '../../utils/types';
import classNames from '../../utils/classNames';

const base = 'no-underline';
const baseDisabled = 'disabled:opacity-50 disabled:pointer-events-none';
const baseStandard = `${base} px-10 py-3.5 no-underline`;
const baseCompact = `${base} px-6 py-1 rounded-lg text-sm`;
const baseCompactTall = `${baseCompact} py-3.5 hover:brightness-90 ${baseDisabled}`;

export const VARIANT_CLASSES = {
    base,
    primary: `${baseStandard} bg-primary rounded`,
    mutedPrimary: `${baseStandard} bg-primary/60 hover:bg-primary/80 text-gray-800 rounded`,
    secondary: `${baseStandard} bg-secondary rounded`,
    exportButton: `${baseStandard} bg-primary hover:bg-button-hover rounded-none`,
    outlineButton: `${baseStandard} hover:bg-white hover:text-black border-white border rounded`,
    downloadButton: `${baseCompact} bg-primary`,
    downloadButtonSecondary: `${baseCompact} bg-secondary`,
    action: `${baseCompactTall} bg-primary`,
    neutral: `${baseCompactTall} bg-white border border-gray-300 text-gray-700`,
    destructive: `${baseCompactTall} bg-error text-white`,
} as const;

type VariantKey = keyof typeof VARIANT_CLASSES;

type BaseButtonProps = {
    variant?: VariantKey;
    flex?: boolean;
};

/** Links to a page outside the single-page app, e.g. a Django page or a download. */
type AnchorButtonProps = BaseButtonProps &
    AnchorHTMLAttributes<HTMLAnchorElement> & {
        href: string;
        to?: never;
    };

/** Navigates within the single-page app through React Router. */
type RouterLinkButtonProps = BaseButtonProps &
    LinkProps & {
        href?: never;
    };

export type RegularButtonProps = BaseButtonProps &
    ButtonHTMLAttributes<HTMLButtonElement> & {
        href?: never;
        to?: never;
    };

export type ButtonProps = AnchorButtonProps | RouterLinkButtonProps | RegularButtonProps;

function getButtonElement({ to, href }: { to?: LinkProps['to']; href?: string }) {
    if (to !== undefined) {
        return Link;
    }
    return href ? 'a' : 'button';
}

const Button = forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonProps>(
    ({ variant = 'primary', flex = false, className, ...props }, ref) => {
        const ButtonOrAnchor = getButtonElement(props);
        return (
            <ButtonOrAnchor
                // We need to ignore this error because the ref type depends on
                // whether it's a button or an anchor.
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-expect-error
                ref={ref}
                className={classNames(VARIANT_CLASSES[variant], { 'flex-1': flex }, className)}
                {...props}
            />
        );
    }
);

Button.displayName = 'Button';

export default Button;

export type LoadingButtonProps = ButtonProps & {
    isLoading?: boolean;
    label?: string;
};

export const LoadingButton = forwardRef<HTMLButtonElement | HTMLAnchorElement, LoadingButtonProps>(
    ({ isLoading, label, className, ...props }, ref) => (
        <Button
            ref={ref}
            {...props}
            className={classNames('relative', className)}
            aria-disabled={isLoading}
            aria-busy={isLoading}
        >
            {isLoading && (
                <div className="absolute inset-0 flex items-center justify-center">
                    <LoadingSpinner />
                </div>
            )}
            <span className={classNames(isLoading && 'invisible')}>{label ?? gettext('SAVE')}</span>
        </Button>
    )
);

LoadingButton.displayName = 'LoadingButton';

const roundedButtonVariant = {
    default: 'text-xs px-7 py-1.5',
    icon: 'text-xs px-3 py-1.5 flex items-center gap-x-0.5',
    big: 'px-8 py-1.5',
} as const;

const roundedButtonColor = {
    primary: 'bg-primary',
    secondary: 'bg-white border border-primary',
} as const;

type RoundedButtonVariant = keyof typeof roundedButtonVariant;
type RoundedButtonColor = keyof typeof roundedButtonColor;

export type RoundedButtonProps = Merge<
    ButtonProps,
    {
        variant?: RoundedButtonVariant;
        color?: RoundedButtonColor;
        disabled?: boolean;
    }
>;

export function RoundedButton({
    children,
    variant = 'default',
    color = 'primary',
    className = '',
    ...props
}: RoundedButtonProps) {
    return (
        <Button
            {...(props as ButtonProps)}
            variant="base"
            className={classNames(
                'rounded-full text-black',
                roundedButtonColor[color],
                roundedButtonVariant[variant],
                className
            )}
        >
            {children}
        </Button>
    );
}

interface ButtonContainerProps {
    children: ReactNode;
}

export function ButtonContainer({ children }: ButtonContainerProps) {
    return <div className="flex space-x-2">{children}</div>;
}

interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    ariaLabel?: string;
    onClick?: MouseEventHandler<HTMLButtonElement>;
    icon: IconProp;
    className?: string;
    buttonClassName?: string;
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
    ({ ariaLabel, onClick, icon, className, buttonClassName, ...props }, ref) => (
        <button
            ref={ref}
            type="button"
            onClick={onClick}
            aria-label={ariaLabel}
            className={buttonClassName}
            {...props}
        >
            <FontAwesomeIcon icon={icon} className={className} />
        </button>
    )
);

IconButton.displayName = 'IconButton';
